import {
  Button,
  TextField,
  Theme,
  Typography,
  createStyles,
  makeStyles
} from '@material-ui/core';
import { EditRounded, UpdateRounded } from '@material-ui/icons';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../../hooks/useAppRudux';
import loadingSlice from '../../pages/Loading/loadingSlice';
import {
  getProjectDocumentRevisionAsync,
  updateRevisionNoteAndLinkAsync
} from '../../pages/ProjectDocumentRevision/projectDocumentRevisionSlice';

interface RevisionNoteProps {
  value: string | null;
  documentRevisionId: number;
  canEdit?: boolean;
  disabledModified?: boolean;
}

export default function RevisionNote({
  value,
  documentRevisionId: id,
  canEdit = false,
  disabledModified = false
}: RevisionNoteProps) {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [note, setNote] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const maxCharacters = 1000;
  const error = (note ? note.length : 0) > maxCharacters;

  function handleEditNote(edit: boolean): () => void {
    return function () {
      if (edit) setNote(value);
      setIsEditMode(edit);
    };
  }

  async function onSubmit(
    event: React.FormEvent<HTMLFormElement>
  ): Promise<void> {
    event.preventDefault();
    try {
      setLoading(true);
      await dispatch(updateRevisionNoteAndLinkAsync({ id, note })).unwrap();
      await dispatch(getProjectDocumentRevisionAsync(id)).unwrap();
      dispatch(
        loadingSlice.actions.success({
          message: t('revisionNote.messages.updated')
        })
      );
      setIsEditMode(false);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    } catch {
      setLoading(false);
    }
  }

  return (
    <div>
      {isEditMode ? (
        <form onSubmit={onSubmit}>
          <TextField
            autoFocus
            id="revision-note"
            variant="outlined"
            size="small"
            multiline
            fullWidth
            placeholder={t('revisionNote.placeholder', { maxCharacters }) as string}
            helperText={t('revisionNote.characterCount', { currentCount: note ? note.length : 0, maxCharacters })}
            error={error}
            value={note ? note : ''}
            onChange={(event) => {
              setNote(event.target.value === '' ? null : event.target.value);
            }}
          />
          <div className={classes.actionArea}>
            <Button
              size="small"
              disabled={loading}
              onClick={handleEditNote(false)}
            >
              {t('revisionNote.buttons.cancel')}
            </Button>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              size="small"
              startIcon={<UpdateRounded />}
              disabled={value === note || error || loading}
            >
              {t('revisionNote.buttons.update')}
            </Button>
          </div>
        </form>
      ) : value ? (
        <div>
          <Typography className={classes.noteText}>{value}</Typography>
          {canEdit && (
            <div className={classes.actionArea}>
              <Button
                color="primary"
                variant="outlined"
                size="small"
                startIcon={<EditRounded />}
                onClick={handleEditNote(true)}
              >
                {t('revisionNote.buttons.edit')}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className={classes.fileNotFound}>
          {t('revisionNote.noteNotFound')}
          {canEdit && (
            <>
              <br />
              <Button
                color="primary"
                variant="outlined"
                size="small"
                onClick={handleEditNote(true)}
                disabled={disabledModified}
              >
                {t('revisionNote.buttons.addNote')}
              </Button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    noteText: {
      whiteSpace: 'pre-wrap'
    },
    actionArea: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: theme.spacing(1),
      gap: theme.spacing(1)
    },
    fileNotFound: {
      padding: theme.spacing(1, 2, 3, 2),
      textAlign: 'center'
    }
  })
);
